import React from "react";

import { faCheck, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { commitLocalUpdate, commitMutation, graphql, type RecordSourceSelectorProxy } from "relay-runtime";

import { environment } from "~/models";
import { Button } from "~/ui";

import type { BlockID } from "./types";

import { type AcceptDismissPopoverAcceptMutation } from "~/__generated__/AcceptDismissPopoverAcceptMutation.graphql";
import type { AcceptDismissPopoverDismissMutation } from "~/__generated__/AcceptDismissPopoverDismissMutation.graphql";

import styles from "./AcceptDismissPopover.module.css";

const acceptActionItemMutation = graphql`
	mutation AcceptDismissPopoverAcceptMutation($actionItemIds: [BlockID!]!) {
		acceptAiActionItems(actionItemIds: $actionItemIds) {
			ok
		}
	}
`;

const dismissActionItemMutation = graphql`
	mutation AcceptDismissPopoverDismissMutation($actionItemIds: [BlockID!]!) {
		dismissAiActionItems(actionItemIds: $actionItemIds) {
			ok
		}
	}
`;

function updateRelayStoreForAcceptance(store: RecordSourceSelectorProxy<object>, actionItemId: string) {
	const node = store.get(actionItemId);
	node?.setValue(false, "aiGenerated");
}

function updateRelayStoreForDeletion(store: RecordSourceSelectorProxy<object>, actionItemId: string) {
	store.delete(actionItemId);
}

/**
 * Converts an AI-generated action item to a normal action item
 *
 * @param blockData Either the global ID of the NoteBlock or the blockKey and noteID (if this is triggered from Prose)
 * @param callbackFn Optional callback after mutation has completed
 */
export function acceptAIActionItems(blockData: BlockID[], callbackFn?: () => void) {
	commitMutation<AcceptDismissPopoverAcceptMutation>(environment, {
		mutation: acceptActionItemMutation,
		variables: { actionItemIds: blockData },

		// We want to update the aiGenerated value in the relay store in the optimisticUpdater so that the UI reflects
		// the change right away. We also need to perform this update once the mutation has completed because the
		// mutation response will override the optimistic update. Since the mutation kicks off an async celery task to
		// perform the db operations, the refetched data after the mutation response will override the value to
		// aiGenerated = true.

		// We only update the store if this function has been called with a NoteBlock ID, which indicates that it's NOT
		// coming from prose. In a prose editor, we will see updates right away as the Note listens for backend changes.
		// Because of these backend updates, trying to edit the relay state can result in UI bugs (menu flickering).

		onCompleted: (_response, errors) => {
			if (errors) {
				return;
			}
			commitLocalUpdate(environment, store => {
				blockData.forEach(blockId => {
					if ("id" in blockId && blockId.id) {
						updateRelayStoreForAcceptance(store, blockId.id);
					}
				});
			});
			callbackFn?.();
		},
		optimisticUpdater: store => {
			blockData.forEach(blockId => {
				if ("id" in blockId && blockId.id) {
					updateRelayStoreForAcceptance(store, blockId.id);
				}
			});
		},
	});
}

/**
 * Deletes the AI-generated action item altogether
 *
 * @param blockData Either the global ID of the NoteBlock or the blockKey and noteID (if this is triggered from Prose)
 * @param callbackFn Optional callback after mutation has completed
 */
export function dismissAIActionItems(blockData: BlockID[], callbackFn?: () => void) {
	commitMutation<AcceptDismissPopoverDismissMutation>(environment, {
		mutation: dismissActionItemMutation,
		variables: { actionItemIds: blockData },
		// See comment in accept() for why we're modifying store records twice
		onCompleted: (_response, errors) => {
			if (errors) {
				return;
			}
			commitLocalUpdate(environment, store => {
				blockData.forEach(blockId => {
					if ("id" in blockId && blockId.id) {
						updateRelayStoreForDeletion(store, blockId.id);
					}
				});
			});
			callbackFn?.();
		},
		optimisticUpdater: store => {
			blockData.forEach(blockId => {
				if ("id" in blockId && blockId.id) {
					updateRelayStoreForDeletion(store, blockId.id);
				}
			});
		},
	});
}

export default function AcceptDismissPopover({
	actionItemId,
	floatingRef,
	floatingStyles,
}: {
	actionItemId: string;
	floatingRef: (node: HTMLElement | null) => void;
	floatingStyles: React.CSSProperties;
}) {
	const accept = React.useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			acceptAIActionItems([{ id: actionItemId }]);
		},
		[actionItemId],
	);

	const dismiss = React.useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			dismissAIActionItems([{ id: actionItemId }]);
		},
		[actionItemId],
	);

	return (
		<div ref={floatingRef} className={styles.popover} style={floatingStyles} key={actionItemId}>
			<Button className={styles.acceptDismissButton} onClick={dismiss}>
				<FontAwesomeIcon icon={faTrashCan} />
				Dismiss
			</Button>
			<Button className={styles.acceptDismissButton} onClick={accept}>
				<FontAwesomeIcon icon={faCheck} />
				Accept
			</Button>
		</div>
	);
}
